import { debug as logger } from 'application/Shared/Utils';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Parses a JSON string from URL parameters.
 *
 * @param key - The key to look up in URL parameters.
 * @returns The parsed value or undefined if not found or invalid.
 */
function parseParam<T>({
    property,
    searchParams,
    tableName
}: {
    property: string;
    searchParams: URLSearchParams;
    tableName: string;
}): T | undefined {
    const stateProperty = searchParams.get(`${tableName}.${property}`);
    if (stateProperty === null) {
        return undefined;
    }

    try {
        return JSON.parse(stateProperty) as T;
    } catch (error) {
        logger.warn(`Invalid JSON for ${property} in URL parameters:`, error);
        return undefined;
    }
}

/**
 * A custom hook that extracts and parses table state from URL parameters.
 *
 * @param tableName - The name of the table, used as a prefix for URL parameters.
 * @returns An object containing the parsed table state from URL parameters.
 */
export const useGetTableViewFromURL = (
    tableName: string
): string | undefined => {
    // Initialize state to store the parsed table state
    const [tableViewFromURL, setTableViewFromURL] = useState<
        string | undefined
    >(undefined);

    const [searchParams] = useSearchParams();

    useEffect(() => {
        // Extract and parse each table state property from URL parameters
        const tableView = parseParam<string>({
            property: 'tableView',
            searchParams,
            tableName
        });

        // Update the state with the parsed values, we set only the values that are not undefined
        setTableViewFromURL(tableView);
    }, [searchParams, tableName]);

    return tableViewFromURL;
};
